import { Injectable, inject } from '@angular/core';
import { GraphQLClientService } from '../../../../../../core/services/graphql-client.service';
import { isErr, ok } from '../../../../../../core/models/api-result';
import type { ApiResult } from '../../../../../../core/models/api-result';
import type { SearchApiEmployee, SearchEntityResult } from '../../../../../../core/models/search-api.model';
import { displayNameFromEmail } from '../../../../../../core/utils/text';
import type { CmdkItem, CmdkPersonItem } from '../../models/cmdk.model';
import type { CmdkProviderContext } from '../../models/cmdk-provider';
import { CmdkStateService } from '../cmdk-state.service';
import { CmdkRemoteProviderBase } from './remote.provider.base';

/**
 * RESPONSIBILITY: the "People" scope — find a colleague and see where they are.
 *
 * BACKEND CONTRACT: `searchMap(locationId, query, types: ["employee"], limit)`
 * — the same operation the space provider uses, with a different types
 * literal — when a building is focused, because it also says where each
 * person is booked today. Without one, `searchEntities(types: ["employee"])`,
 * which since 9 Oct 2026 runs the SAME directory search on the server (Atlas,
 * the directory's exclusions — no room mailboxes — and the same name
 * ranking). This provider used to stand down with no building, so People
 * found nobody on a page without a site.
 *
 * AI CONTEXT
 *
 * **Not `PersonSearchService`.** That service is owned by the map's people
 * panel and keeps results in shared signals with its own RxJS debounce; the
 * palette pushing into it would have the two surfaces overwrite each other
 * mid-keystroke. Only the shape of the query is reused.
 *
 * **Presence is `unknown`, deliberately.** `searchMap` returns
 * `currentBooking` — where someone is sitting right now — but nothing about
 * whether they are in the office, at home or away. Mapping "has a desk booking
 * today" to `office` would be a guess rendered as a status dot, and a wrong
 * dot next to a colleague's name is worse than no dot. The booking is used for
 * the location line, which is what it actually is.
 */
const CMDK_PEOPLE_QUERY = `
  query cmdkPeople($locationId: ID!, $query: String!, $limit: Int) {
    searchMap(
      locationId: $locationId
      query: $query
      types: ["employee"]
      limit: $limit
      includeSummary: false
      lexicalOnly: true
    ) {
      employees {
        id
        firstName
        lastName
        name
        email
        department
        jobTitle
        score
        currentBooking { spaceName floorName }
      }
    }
  }
`;

/** No building focused: the directory, estate-wide. No location line. */
const CMDK_PEOPLE_ANYWHERE_QUERY = `
  query cmdkPeopleAnywhere($query: String!, $limit: Int) {
    searchEntities(query: $query, types: ["employee"], limit: $limit) {
      id
      name
      type
      subtype
      score
    }
  }
`;

const LIMIT = 8;

@Injectable({ providedIn: 'root' })
export class CmdkPersonProvider extends CmdkRemoteProviderBase {
  private readonly graphql = inject(GraphQLClientService);
  /** Optional: a provider built outside the app (its spec) has no palette, and so no harness. */
  private readonly palette = inject(CmdkStateService, { optional: true });

  readonly id = 'person';
  readonly kind = 'person' as const;
  readonly scope = 'people' as const;
  /** Two is enough here: a name prefix is specific in a way "bo" is not. */
  readonly minQueryLength = 2;

  isEnabled(): boolean {
    // No action gates colleague lookup — the directory is what the People
    // panel already shows every viewer.
    return true;
  }

  protected async fetch(ctx: CmdkProviderContext): Promise<ApiResult<CmdkItem[]>> {
    // A harness's people (`/preview/cmdk?fixtures=call`): matched on the name, nothing sent.
    const fixtures = this.palette?.harness()?.people;
    if (fixtures) {
      const q = ctx.query.toLowerCase();
      return ok(fixtures.filter((p) => p.title.toLowerCase().includes(q)).slice(0, LIMIT));
    }
    if (!ctx.locationId) return this.fetchAnywhere(ctx);

    const result = await this.graphql.queryResult<{ employees?: SearchApiEmployee[] }>(
      CMDK_PEOPLE_QUERY,
      { locationId: ctx.locationId, query: ctx.query, limit: LIMIT },
    );
    if (isErr(result)) return result;
    return ok((result.data?.employees ?? []).map(e => toItem(e)));
  }

  private async fetchAnywhere(ctx: CmdkProviderContext): Promise<ApiResult<CmdkItem[]>> {
    const result = await this.graphql.queryResult<SearchEntityResult[]>(
      CMDK_PEOPLE_ANYWHERE_QUERY,
      { query: ctx.query, limit: LIMIT },
    );
    if (isErr(result)) return result;
    return ok((result.data ?? []).filter(e => e.type === 'employee').map(fromEntity));
  }
}

function fromEntity(entity: SearchEntityResult): CmdkPersonItem {
  return {
    id: entity.id,
    kind: 'person',
    title: entity.name,
    subtitle: entity.subtype || undefined,
    department: entity.subtype || undefined,
    presence: 'unknown',
  };
}

function toItem(employee: SearchApiEmployee): CmdkPersonItem {
  const name = fullName(employee);
  const booking = employee.currentBooking;
  return {
    id: employee.id,
    kind: 'person',
    title: name,
    subtitle: join([employee.jobTitle, employee.department]),
    // Email is matchable but never rendered — people search for a colleague
    // by the address they have in their inbox at least as often as by name.
    aliases: [employee.email].filter((v): v is string => !!v),
    email: employee.email,
    jobTitle: employee.jobTitle,
    department: employee.department,
    presence: 'unknown',
    locationLabel: join([booking?.floorName, booking?.spaceName]),
  };
}

/**
 * A display name that is never blank. The API populates `name` for most
 * tenants but not all; where it is missing the parts are assembled, and where
 * even those are missing the local part of the address is a better label than
 * an empty row.
 */
function fullName(employee: SearchApiEmployee): string {
  if (employee.name?.trim()) return employee.name.trim();
  const parts = [employee.firstName, employee.lastName].filter(Boolean).join(' ').trim();
  if (parts) return parts;
  return (employee.email ? displayNameFromEmail(employee.email) : null) ?? employee.id;
}

function join(parts: readonly (string | undefined)[]): string | undefined {
  const kept = parts.filter((p): p is string => !!p);
  return kept.length ? kept.join(' · ') : undefined;
}
